<script setup lang="ts">
import {
  BoxComponent,
  CONTENT_ITEM_CARD_WIDTH,
  ContentItem,
  type ContentItemsFilter,
  FlexComponent,
  PaginationComponent,
  TypographyComponent,
  useContentItemNavigation,
} from '@metapic-apps/shared'
import { ElIcon } from 'element-plus'
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useGetContentItems } from '@/api/content-items.api'
import LoadingIcon from '@/assets/icons/loading.svg?component'
import ContentItemOverview from '@/components/ContentItemOverview/ContentItemOverview.vue'
import type { AdvertiserType } from '@/lib/types/advertiser.types'

const props = defineProps<{
  advertiser: AdvertiserType | undefined
}>()

const filter = defineModel<ContentItemsFilter>({ required: true })
const route = useRoute()
const router = useRouter()

const advertiserRef = computed(() => props.advertiser)
const { data: contentItems, isLoading, totalPages } = useGetContentItems(filter, advertiserRef)

const selectedContentItemId = computed(() => route.query.contentItemId as string)
const minCardWidthPx = `${CONTENT_ITEM_CARD_WIDTH}px`

const openContentItemOverview = (id: string) => {
  void router.push({ query: { ...route.query, contentItemId: id } })
}

const handleCloseOverview = async () => {
  await router.push({ query: { ...route.query, contentItemId: undefined } })
}

const { hasPrevItem, hasNextItem, handleNavigate } = useContentItemNavigation({
  items: contentItems,
  totalPages,
  getPage: () => filter.value.page ?? 1,
  setPage: (page) => {
    filter.value.page = page
  },
})
</script>

<template>
  <FlexComponent v-if="!isLoading && contentItems && contentItems?.length === 0" justify="center">
    <TypographyComponent variant="body-large"> There are no media that match your filters </TypographyComponent>
  </FlexComponent>
  <div v-if="isLoading" class="content-library__loading">
    <ElIcon :size="96">
      <LoadingIcon />
    </ElIcon>
  </div>
  <div class="content-library__grid">
    <ContentItem v-for="item in contentItems" :key="item.id" :item="item" view-mode="advertiser" @on-open="openContentItemOverview" />
  </div>
  <BoxComponent v-if="totalPages !== 0" :pt="32" :pb="32">
    <PaginationComponent v-model:current-page="filter.page" :total-pages="totalPages" />
  </BoxComponent>
  <ContentItemOverview
    v-model="selectedContentItemId"
    :has-prev-item="hasPrevItem"
    :has-next-item="hasNextItem"
    @on-close="handleCloseOverview"
    @navigate="handleNavigate"
  />
</template>

<style scoped lang="scss">
.content-library {
  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(v-bind(minCardWidthPx), 1fr));
    gap: 16px;
    width: 100%;

    @media (min-width: 1440px) {
      grid-template-columns: repeat(4, 1fr);
    }
  }

  &__loading {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
}
</style>
